import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Badge,
  Divider,
  LinearProgress,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DeviceFileBrowser from '../DeviceFileBrowser';
import formatBytes from '../../utils/formatBytes';

// ---------------------------------------------------------------------------

const CATEGORIES = [
  { key: 'passive', label: 'Background', directory: 'data/passive' },
  { key: 'captures', label: 'Captures', directory: 'data/captures' },
  { key: 'logs', label: 'System Logs', directory: 'data/logs' },
];

const EMPTY_FOLDER = { entries: [], total: 0, page: 0, loaded: false, loading: false };

/**
 * The Files tab holds ONE page of ONE folder. It never sees a whole directory:
 * the parent fetches a window at a time (`onActivate` / `onPageChange`) so a
 * card with thousands of files neither stalls the serial link nor lands
 * thousands of rows in the DOM.
 */
export default function FilesTab({
  folders,
  pageSize,
  onActivate,
  onPageChange,
  onDownload,
  onDelete,
  downloading,
  onRefresh,
  storageInfo,
  refreshing,
}) {
  const [activeCategory, setActiveCategory] = useState('passive');

  // Selecting a folder — including landing on the default one when the tab
  // opens — is what asks the device for its first page.
  useEffect(() => {
    onActivate(activeCategory);
  }, [activeCategory, onActivate]);

  const activeCat = CATEGORIES.find((c) => c.key === activeCategory);
  const folder = folders[activeCategory] || EMPTY_FOLDER;
  const { entries, total, page, loading } = folder;

  const firstIndex = page * pageSize + 1;
  const lastIndex = page * pageSize + entries.length;
  const hasMore = lastIndex < total;
  const showPager = total > pageSize;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0,
        height: 400,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: 200,
          flexShrink: 0,
          borderRight: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Folders
          </Typography>
        </Box>
        <Divider />
        <List dense disablePadding>
          {CATEGORIES.map((cat) => {
            // A folder nobody has opened yet has no count to show — the badge
            // stays empty rather than claiming zero files.
            const counted = folders[cat.key] || EMPTY_FOLDER;
            return (
              <ListItemButton
                key={cat.key}
                selected={activeCategory === cat.key}
                onClick={() => setActiveCategory(cat.key)}
                sx={{ py: 0.75 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Badge
                    badgeContent={counted.loaded ? counted.total : 0}
                    color="primary"
                    max={9999}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', minWidth: 18, height: 18 } }}
                  >
                    <FolderIcon fontSize="small" />
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={cat.label}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItemButton>
            );
          })}
        </List>

        {/* Storage info at bottom */}
        {storageInfo && (
          <Box sx={{ px: 1.5, py: 1.5, mt: 'auto' }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              Storage
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {formatBytes(storageInfo.used)} / {formatBytes(storageInfo.total)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={storageInfo.total > 0 ? (storageInfo.used / storageInfo.total) * 100 : 0}
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Box>
        )}
      </Box>

      {/* File table */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" fontWeight="bold">
            {activeCat?.label}
          </Typography>
          <Tooltip title={`Refresh ${activeCat?.label}`}>
            <span>
              <IconButton
                size="small"
                aria-label={`Refresh ${activeCat?.label}`}
                onClick={() => onRefresh(activeCategory)}
                disabled={refreshing || loading}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={refreshing ? { animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } } : {}}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <LinearProgress />
          ) : (
            <DeviceFileBrowser
              files={entries}
              directory={activeCat?.directory || ''}
              onDownload={onDownload}
              onDelete={onDelete}
              downloading={downloading}
            />
          )}
        </Box>
        {showPager && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              px: 1.5,
              py: 0.5,
              borderTop: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {`${firstIndex}–${lastIndex} of ${total}`}
            </Typography>
            <IconButton
              size="small"
              aria-label="Previous page"
              disabled={page === 0 || loading}
              onClick={() => onPageChange(activeCategory, page - 1)}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Next page"
              disabled={!hasMore || loading}
              onClick={() => onPageChange(activeCategory, page + 1)}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}

const folderShape = PropTypes.shape({
  entries: PropTypes.array,
  total: PropTypes.number,
  page: PropTypes.number,
  loaded: PropTypes.bool,
  loading: PropTypes.bool,
});

FilesTab.propTypes = {
  folders: PropTypes.shape({
    passive: folderShape,
    captures: folderShape,
    logs: folderShape,
  }).isRequired,
  pageSize: PropTypes.number.isRequired,
  onActivate: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  downloading: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  storageInfo: PropTypes.object,
  refreshing: PropTypes.bool,
};
